import { decodeRle, encodeRle } from './codec';
import { AreaMapFileSchema, CELL_MASK, CELL_WALK, CELL_WALL_N, CELL_WALL_W } from './format';
import { computeNavVersion } from './navVersion';
/**
 * Parses and validates area-map JSON (already JSON.parse'd, or a bundler
 * JSON import). Works in Node and the browser. Throws if the shape is wrong,
 * the walk mask does not decode to w*h cells, or `navVersion` does not match
 * the decoded cells.
 */
export function parseAreaMap(json) {
    const file = AreaMapFileSchema.parse(json);
    const { w, h } = file;
    const cells = decodeRle(file.walk, w * h);
    for (let i = 0; i < cells.length; i++) {
        if ((cells[i] & ~CELL_MASK) !== 0)
            throw new Error(`walk: unknown bits at cell ${i}`);
    }
    const nav = computeNavVersion(w, h, cells);
    if (nav !== file.navVersion) {
        throw new Error(`area map ${file.id}: navVersion ${file.navVersion} does not match walk data (${nav}); rebuild the map`);
    }
    const inBounds = (x, y) => x >= 0 && y >= 0 && x < w && y < h;
    return {
        ...file,
        cells,
        inBounds,
        isWalkable: (x, y) => inBounds(x, y) && (cells[y * w + x] & CELL_WALK) !== 0,
        hasWall: (x, y, edge) => inBounds(x, y) && (cells[y * w + x] & (edge === 'W' ? CELL_WALL_W : CELL_WALL_N)) !== 0,
    };
}
/** Fetches and parses a map (browser, or Node 22's global fetch). */
export async function fetchAreaMap(url) {
    const res = await fetch(url);
    if (!res.ok)
        throw new Error(`area map ${url}: HTTP ${res.status}`);
    return parseAreaMap(await res.json());
}
/**
 * Builds the on-disk object from raw cells (used by the map build script).
 * `navVersion` and `walk` are derived; everything else is passed through.
 */
export function makeAreaMapFile(base, cells) {
    if (cells.length !== base.w * base.h)
        throw new Error('makeAreaMapFile: cells length != w*h');
    return {
        id: base.id,
        w: base.w,
        h: base.h,
        navVersion: computeNavVersion(base.w, base.h, cells),
        walk: encodeRle(cells),
        objects: base.objects,
        doors: base.doors,
        stalls: base.stalls,
        pois: base.pois,
        spawn: base.spawn,
    };
}
/**
 * Applies a parsed map's walk data to an engine TileGrid (structural, so
 * shared does not import the engine). Call on a fresh grid: non-walkable
 * tiles get one static block, wall bits set edge walls.
 */
export function applyAreaMapToGrid(map, grid) {
    if (grid.width !== map.w || grid.height !== map.h)
        throw new Error('applyAreaMapToGrid: size mismatch');
    for (let y = 0; y < map.h; y++) {
        for (let x = 0; x < map.w; x++) {
            const c = map.cells[y * map.w + x];
            if ((c & CELL_WALK) === 0)
                grid.blockFootprint(x, y, 1, 1, true);
            if (c & CELL_WALL_W)
                grid.setWall(x, y, 'W', true);
            if (c & CELL_WALL_N)
                grid.setWall(x, y, 'N', true);
        }
    }
}
